/**
 * Resolves ARIA property values handling both HTML-style (aria-*) and React-style (aria*) naming.
 *
 * ARIA properties and JSX have inconsistencies:
 * - DOM elements: JSX prefers HTML property style (kebab-case: aria-label)
 * - React Elements: JSX allows React property style (camelCase: ariaLabel)
 *
 * This function prefers the HTML style if present, falling back to React style.
 * Warns in console if both are provided.
 *
 * @param componentName - Name of the component (for warning messages)
 * @param htmlProperty - HTML-style property name (e.g., 'aria-label')
 * @param reactProperty - React-style property name (e.g., 'ariaLabel')
 * @param props - The component props object
 * @returns The resolved property value
 */
export function resolveAriaProperty(componentName: string, htmlProperty: string, reactProperty: string, props: any): any {
  // ARIA properties and JSX get complex and can cause inconsistencies. This function tries to resolve them.
  // For DOM elements, JSX prefers the HTML property style (the snake-case aria-* property names).
  // For React Elements, JSX prefers the React property style (the camelCase aria* property names).
  // This function prefers the HTML style, if present, and uses the React style as a fallback.
  if (props?.hasOwnProperty(htmlProperty) && props?.hasOwnProperty(reactProperty)) {
    console.warn(`Accessibility: Component '${componentName}', id: '${props.id}' provided both ${htmlProperty} and ${reactProperty}. ${htmlProperty} was used. Please remove the duplicate property.`);
    return props[htmlProperty];
  }
  else if (props?.hasOwnProperty(reactProperty)) {
    return props[reactProperty];
  }
  else {
    return props[htmlProperty];
  }
}

export default resolveAriaProperty;
